<template>
  <div ref="top" class="min-h-[3.75rem]">
    <HeaderScrollBoundary />
    <div class="pointer-events-none absolute inset-0 mx-auto">
      <div
        ref="filterBarHeader"
        class="pointer-events-auto sticky -top-px z-20 border-b border-border-light px-5 py-2 md:px-0"
        :class="{
          'bg-background': hasScrolled,
          'sm:invisible': !isMounted,
        }"
      >
        <div class="h-container relative">
          <div ref="filterBarItems" class="flex justify-center gap-2 overflow-hidden">
            <FilterDropdown
              ref="filterSortBy"
              :class="{ 'invisible absolute': !showSortDropdown }"
              class="my-auto hidden shrink-0 sm:block"
              :has-active-filter="orderBy !== defaultSortOrder"
            >
              <template #default>
                <span class="grid">
                  <span v-for="(name, orderByOption) in props.orderByOptions" :key="orderByOption" class="col-[1] row-[1]" :class="{ invisible: props.orderBy !== orderByOption }">
                    {{ t['w.module.filter_bar.order_by']() }}: <span class="font-medium">{{ name }}</span>
                  </span>
                </span>
              </template>
              <template #content>
                <FilterOrderBy v-model="orderByComputed" smaller-text :options="props.orderByOptions" />
              </template>
            </FilterDropdown>
            <FilterDropdown
              v-for="(filter, index) of filters"
              :key="`filter-${filter.id}`"
              ref="filterDropdowns"
              :class="{ 'invisible absolute': index >= numberOfFiltersVisible }"
              class="hidden shrink-0 sm:block"
              :has-active-filter="hasActiveFilters(filter)"
            >
              <template #default>
                {{ filter.name }}
              </template>
              <template #content>
                <FilterCheckbox
                  v-if="filter.type === 'choice'"
                  :group="filter.id"
                  :title="filter.name"
                  :options="filter.properties.options"
                  :applied-filters="(props.appliedFilters[filter.id] as Choices | undefined) ?? { choices: [] }"
                  smaller-text
                  @update-choices="(group: number, choices: number[]) => emit('updateChoices', group, choices)"
                />
                <FilterRange
                  v-else-if="filter.type === 'range'"
                  :group="filter.id"
                  :title="filter.name"
                  :min="filter.properties.min"
                  :max="filter.properties.max"
                  :applied-filters="(props.appliedFilters[filter.id] as Range | undefined) ?? { min: filter.properties.min, max: filter.properties.max }"
                  :interval="filter.properties.interval"
                  @update-range="(group: number, min: number, max: number) => emit('updateRange', group, min, max)"
                />
              </template>
            </FilterDropdown>
            <Button
              ref="showAllButton"
              class="btn-flat btn-rounded shrink-0 text-button-l sm:bg-background-medium sm:text-button-m"
              :class="{ 'sm:invisible sm:absolute': !showAllFiltersButton }"
              @click="isFilterOpen = true"
            >
              <span class="hidden sm:block">{{ t['w.module.filter_bar.show_all']() }}</span>
              <span class="flex items-center sm:hidden">
                {{ t['w.module.filter_bar.filter_and_order']() }}
                <Icon class="size-6" name="chevron-down" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as t from '~/messages'
import Button from '~/components/button/Button.vue'
import FilterDropdown from '~/components/filter-bar/FilterDropdown.vue'
import FilterOrderBy from '~/components/filter-bar/FilterOrderBy.vue'
import FilterCheckbox from '~/components/filter-bar/FilterCheckbox.vue'
import FilterRange from '~/components/filter-bar/FilterRange.vue'
import Icon from '~/components/icon/Icon.vue'
import type { Filter } from '~/components/filter-bar/filter'
import { useIsFilterOpenState } from '~/composables/states'
import HeaderScrollBoundary from '~/components/header/HeaderScrollBoundary.vue'

import { useIntersectionObserver } from '@vueuse/core'

const props = defineProps<{
  filters: Filter[]
  appliedFilters: Record<string, AppliedFilter>
  orderByOptions: Record<string, string>
  defaultSortOrder: string
  orderBy: string
}>()

// Limited to 12 to reduce the amount of dropdowns being rendered that would be always invisible.
const filters = computed(() => props.filters.slice(0, 12))

const isFilterOpen = useIsFilterOpenState()

const hasScrolled = ref(false)

const top = useTemplateRef('top')
const filterBarHeader = useTemplateRef('filterBarHeader')
const filterBarItems = useTemplateRef('filterBarItems')
const filterDropdowns = useTemplateRef('filterDropdowns')
const showAllButton = useTemplateRef('showAllButton')
const filterSortBy = useTemplateRef('filterSortBy')
const numberOfFiltersVisible = ref(0)
const showAllFiltersButton = ref(true)
const showSortDropdown = ref(true)

const orderByComputed = computed({
  get() {
    return props.orderBy
  },
  set(newValue) {
    emit('orderByChanged', newValue)
  },
})

const emit = defineEmits<{
  orderByChanged: [newValue: string]
  updateChoices: [group: number, choices: number[]]
  updateRange: [group: number, min: number, max: number]
}>()

const updateNumberOfFiltersVisible = () => {
  if (!filterBarItems.value || !showAllButton.value || !filterDropdowns.value || !filterSortBy.value) {
    return
  }

  const availableWidth = filterBarItems.value.clientWidth
  const GAP = 8 // px
  const showAllIsOptional = props.filters.length === filters.value.length

  // Case 1: all fit
  if (showAllIsOptional) {
    let elementsWidth = filterSortBy.value.$el.clientWidth
    for (const item of filterDropdowns.value) {
      if (!item) {
        continue
      }

      elementsWidth += GAP + item.$el.clientWidth
    }

    if (availableWidth >= elementsWidth) {
      showSortDropdown.value = true
      numberOfFiltersVisible.value = filterDropdowns.value.length
      showAllFiltersButton.value = false
      return
    }
  }

  // Case 2: some fit
  let elementsWidth = filterSortBy.value.$el.clientWidth + GAP + showAllButton.value.$el.clientWidth

  let count = 0
  for (const item of filterDropdowns.value) {
    if (!item) {
      continue
    }

    const itemWidth = GAP + item.$el.clientWidth

    if (itemWidth + elementsWidth <= availableWidth) {
      elementsWidth += itemWidth
      ++count
    } else {
      break
    }
  }

  showAllFiltersButton.value = true
  if (elementsWidth <= availableWidth) {
    showSortDropdown.value = true
    numberOfFiltersVisible.value = count
  } else {
    // Case 3: only "show all" button fits
    showSortDropdown.value = false
    numberOfFiltersVisible.value = 0
  }
}

interface Choices {
  choices: number[]
}

interface Range {
  min: number
  max: number
}

type AppliedFilter = Choices | Range

const hasActiveFilters = (filter: Filter): boolean => {
  if (filter.type === 'choice') {
    const applied = props.appliedFilters[filter.id] as Choices | undefined
    if (!applied) {
      return false
    }

    return filter.properties.options.some((option) => applied.choices.includes(option.id))
  }

  if (filter.type === 'range') {
    return !!props.appliedFilters[filter.id]
  }

  return false
}

const isMounted = ref(false)

onMounted(() => {
  const throttledResize = useThrottleFrame(updateNumberOfFiltersVisible)
  useEventListener(window, 'resize', throttledResize, { passive: true })
  throttledResize()

  // Update value once before Intersection Observer callback is run to avoid flicker
  if (top.value) {
    hasScrolled.value = top.value.getBoundingClientRect().top < 0
  }

  useIntersectionObserver(top, (entries) => {
    hasScrolled.value = entries.some((e) => e.boundingClientRect.top < 0)
  })

  isMounted.value = true
})

defineExpose({ hasScrolled })
</script>
